/**
 * OP Readiness — Products tab (WP7.3 product list + detail).
 * Read-only: list/filter payloads with their latest audit verdict; row click opens the detail
 * (core summary, field results, payload/snapshot JSON, prices, dispatch history — no controls).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchOPReadinessProducts,
  fetchOPReadinessProductDetail,
  fetchOPReadinessProductPayload,
  fetchOPReadinessProductPrices,
  isUnavailable,
  type ProductRow,
} from '@/lib/op-readiness';
import { StatusBadge, SeverityBadge, ValueCell, JsonBlock, UnavailableNotice, formatDateTime } from './shared';

const READINESS_OPTIONS = ['', 'READY_TO_AUDIT', 'READY_WITH_WARNINGS', 'BLOCKED_MAPPING'];
const AUDIT_OPTIONS = ['', 'READY_TO_WRITE', 'READY_WITH_WARNINGS', 'BLOCKED_MAPPING', 'NOT_FOUND_IN_OP', 'AUDIT_ERROR'];

function ProductDetail({ payloadId }: { payloadId: string }) {
  const [showJson, setShowJson] = useState(false);
  const detailQuery = useQuery({
    queryKey: ['op-readiness', 'detail', payloadId],
    queryFn: () => fetchOPReadinessProductDetail(payloadId),
  });
  const payloadQuery = useQuery({
    queryKey: ['op-readiness', 'payload', payloadId],
    queryFn: () => fetchOPReadinessProductPayload(payloadId),
    enabled: showJson,
  });
  const pricesQuery = useQuery({
    queryKey: ['op-readiness', 'prices', payloadId],
    queryFn: () => fetchOPReadinessProductPrices(payloadId),
  });

  if (detailQuery.isLoading) return <Skeleton className="h-48 w-full rounded-lg" />;
  const detail = detailQuery.data;
  if (!detail || isUnavailable(detail)) return null;
  if (!detail.found) return <p className="text-sm text-muted-foreground">Payload not found.</p>;

  const core = detail.core ?? {};
  const audit = detail.latestAudit;
  const prices = pricesQuery.data && !isUnavailable(pricesQuery.data) ? pricesQuery.data : null;
  const payload = payloadQuery.data && !isUnavailable(payloadQuery.data) ? payloadQuery.data : null;

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{String(detail.payload?.Title ?? '(untitled)')}</h3>
          <p className="text-sm text-muted-foreground">
            EAN {detail.payload?.EAN ?? '—'} · OP id {audit?.OPProductId ?? '—'} ·{' '}
            {String(core.ProductType ?? '')} {String(core.Medium ?? '')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={String(detail.payload?.ReadinessStatus)} />
          {audit && <StatusBadge status={audit.AuditStatus} />}
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm md:grid-cols-4">
        <div><dt className="text-muted-foreground">Imprint</dt><dd>{String(core.Imprint ?? '—')}</dd></div>
        <div><dt className="text-muted-foreground">Language</dt><dd>{String(core.MainLanguage ?? '—')}</dd></div>
        <div><dt className="text-muted-foreground">Copyright</dt><dd>{String(core.CopyrightYear ?? '—')}</dd></div>
        <div><dt className="text-muted-foreground">Last audited</dt><dd>{formatDateTime(audit?.CreatedAt)}</dd></div>
        <div className="col-span-2 md:col-span-4">
          <dt className="text-muted-foreground">Payload hash</dt>
          <dd className="break-all font-mono text-xs">{String(detail.payload?.PayloadHash ?? '—')}</dd>
        </div>
        {detail.payload?.BlockedReason ? (
          <div className="col-span-2 md:col-span-4">
            <dt className="text-muted-foreground">Blocked reason</dt>
            <dd>{String(detail.payload.BlockedReason)} ({String(detail.payload.BlockedReasonCode)})</dd>
          </div>
        ) : null}
      </dl>

      {audit?.fieldResults && (
        <div>
          <h4 className="mb-2 text-sm font-semibold">Latest audit — field comparison vs live OP</h4>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="py-1 pr-3">Field</th><th className="py-1 pr-3">Severity</th>
                <th className="py-1 pr-3">Match</th><th className="py-1 pr-3">Our payload</th>
                <th className="py-1">Live OP</th>
              </tr>
            </thead>
            <tbody>
              {audit.fieldResults.map((f) => (
                <tr key={f.field} className={`border-b last:border-0 ${f.match ? '' : 'bg-red-50/50'}`}>
                  <td className="py-1.5 pr-3 font-mono text-xs">{f.field}</td>
                  <td className="py-1.5 pr-3"><SeverityBadge severity={f.severity} /></td>
                  <td className="py-1.5 pr-3">{f.match ? '✓' : '✗'}</td>
                  <td className="py-1.5 pr-3"><ValueCell value={f.expected} /></td>
                  <td className="py-1.5"><ValueCell value={f.actual} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {prices && (
        <p className="text-sm text-muted-foreground">
          Prices: {prices.total} rows, {prices.blocked} write-blocked — {prices.note}
        </p>
      )}

      {detail.dispatchHistory && detail.dispatchHistory.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold">Dispatch history (read-only — controls land with Stage 7b)</h4>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="py-1 pr-3">When</th><th className="py-1 pr-3">Mode</th>
                <th className="py-1 pr-3">Status</th><th className="py-1 pr-3">Attempts</th>
                <th className="py-1">Result</th>
              </tr>
            </thead>
            <tbody>
              {detail.dispatchHistory.map((d) => (
                <tr key={`${d.DispatchId}-${d.DispatchResultId ?? 'q'}`} className="border-b last:border-0">
                  <td className="py-1.5 pr-3 whitespace-nowrap">{formatDateTime(d.ResultAt ?? d.CreatedAt)}</td>
                  <td className="py-1.5 pr-3">{d.Mode ?? '—'}</td>
                  <td className="py-1.5 pr-3"><StatusBadge status={d.DispatchStatus} /></td>
                  <td className="py-1.5 pr-3">{d.AttemptCount}</td>
                  <td className="py-1.5"><ValueCell value={d.ResultSummaryJson} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <Button size="sm" variant="outline" onClick={() => setShowJson((v) => !v)}>
          {showJson ? 'Hide payload JSON' : 'Show payload JSON'}
        </Button>
        {showJson && payloadQuery.isLoading && <Skeleton className="mt-3 h-40 w-full rounded-md" />}
        {showJson && payload?.found && (
          <div className="mt-3 grid gap-4 lg:grid-cols-2">
            <JsonBlock value={payload.payload} label="Composed payload (mart.OPProductPayload)" />
            {payload.snapshot ? (
              <JsonBlock
                value={payload.snapshot}
                label={`Live-OP pre-read snapshot (${payload.snapshotMode ?? ''} ${formatDateTime(payload.snapshotAt)})`}
              />
            ) : (
              <p className="self-center text-sm text-muted-foreground">
                No pre-read snapshot yet (snapshots are captured by writer dry-runs).
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function ProductsView() {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [auditStatus, setAuditStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);

  const productsQuery = useQuery({
    queryKey: ['op-readiness', 'products', appliedSearch, status, auditStatus, page],
    queryFn: () =>
      fetchOPReadinessProducts({
        search: appliedSearch || undefined,
        status: status || undefined,
        auditStatus: auditStatus || undefined,
        page,
        pageSize: 50,
      }),
  });

  const data = productsQuery.data;
  if (data && isUnavailable(data)) return <UnavailableNotice reason={data.reason} />;
  const rows: ProductRow[] = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setAppliedSearch(search.trim());
        }}
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search EAN or title…"
          className="w-64"
        />
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
        >
          {READINESS_OPTIONS.map((o) => <option key={o} value={o}>{o || 'Any readiness'}</option>)}
        </select>
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={auditStatus}
          onChange={(e) => { setAuditStatus(e.target.value); setPage(1); }}
        >
          {AUDIT_OPTIONS.map((o) => <option key={o} value={o}>{o || 'Any audit status'}</option>)}
        </select>
        <Button type="submit" size="sm">Apply</Button>
        <span className="ml-auto text-sm text-muted-foreground">
          {total.toLocaleString()} payloads · page {page}/{pages}
        </span>
      </form>

      {productsQuery.isLoading ? (
        <Skeleton className="h-64 w-full rounded-lg" />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">EAN</th><th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Lang</th><th className="px-3 py-2">Readiness</th>
                <th className="px-3 py-2">Latest audit</th><th className="px-3 py-2">Mismatches</th>
                <th className="px-3 py-2">Audited</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.OPProductPayloadId}
                  onClick={() => setSelected(selected === r.OPProductPayloadId ? null : r.OPProductPayloadId)}
                  className={`cursor-pointer border-b last:border-0 hover:bg-muted/30 ${selected === r.OPProductPayloadId ? 'bg-muted/40' : ''}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{r.EAN ?? '—'}</td>
                  <td className="max-w-[24rem] truncate px-3 py-2">{r.Title ?? '—'}</td>
                  <td className="px-3 py-2">{r.Language ?? '—'}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.ReadinessStatus} /></td>
                  <td className="px-3 py-2"><StatusBadge status={r.AuditStatus} /></td>
                  <td className="px-3 py-2">{r.AuditMismatchCount ?? '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{formatDateTime(r.LastAuditedAt)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">No payloads match the filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>

      {selected && <ProductDetail payloadId={selected} />}
    </div>
  );
}
